import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useFetch } from "@/hooks/useFetch";
import { AddressCard } from "@/components/order/AddressCard";
import { api, errMsg } from "@/lib/api";

const CUST = "cust:";
const optKey = (c) => `${CUST}${c.source}:${c.ref_id}`;

// Copies a Customer Master site / registered address onto the LM as a dispatch address and returns its id.
async function copyToLm(orderId, c) {
  const body = { source: c.source, customer_site_id: c.source === "customer_site" ? c.ref_id : null, customer_registration_id: c.source === "customer_registration" ? c.ref_id : null };
  const r = await api.post(`/orders/${orderId}/dispatch-addresses`, body);
  return r.data.id;
}

// Dispatch address picker for one LM; preselects the default address and also offers the customer's own addresses.
export function AddressSelect({ orderId, value, onChange, testid, label = "Dispatch address *" }) {
  const { data, reload } = useFetch(orderId ? `/orders/${orderId}/dispatch-addresses?active=true` : null);
  const { data: cust, reload: reloadCust } = useFetch(orderId ? `/orders/${orderId}/customer-addresses` : null);
  const [busy, setBusy] = useState(false);
  const rows = data || [];
  const extra = (cust || []).filter((c) => !c.on_lm_address_id);
  const chosen = rows.find((a) => a.id === value) || (!value && rows.find((a) => a.is_default));
  const auto = chosen && chosen.id !== value ? chosen.id : null;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  useEffect(() => {
    if (auto) onChangeRef.current(auto);
  }, [auto]);
  const pick = async (v) => {
    if (!v.startsWith(CUST)) return onChange(v);
    const c = extra.find((x) => optKey(x) === v);
    setBusy(true);
    try {
      const id = await copyToLm(orderId, c);
      toast.success(`${c.address_label} copied to this LM's dispatch addresses`);
      await Promise.all([reload(), reloadCust()]);
      onChange(id);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-2" data-testid={testid}>
      <div><Label>{label}</Label>
        <Select value={chosen?.id} onValueChange={pick} disabled={busy}>
          <SelectTrigger data-testid={`${testid}-select`}><SelectValue placeholder={busy ? "Copying customer address…" : rows.length || extra.length ? "Pick the dispatch address" : "No addresses — add one on the LM's Dispatch addresses tab"} /></SelectTrigger>
          <SelectContent>
            <SelectGroup><SelectLabel className="text-[10px] uppercase text-slate-500">This LM's dispatch addresses</SelectLabel>
              {rows.map((a) => <SelectItem key={a.id} value={a.id}>{a.address_label}{a.is_default ? " (default)" : ""} · {a.city}{a.gstin ? ` · ${a.gstin}` : ""}</SelectItem>)}</SelectGroup>
            {extra.length > 0 && <SelectGroup data-testid={`${testid}-customer-group`}><SelectLabel className="text-[10px] uppercase text-slate-500">Customer addresses (copied to the LM when picked)</SelectLabel>
              {extra.map((c) => <SelectItem key={optKey(c)} value={optKey(c)} data-testid={`${testid}-cust-${c.ref_id}`}>{c.kind}: {c.address_label} · {c.city || c.state}{c.gstin ? ` · ${c.gstin}` : ""}</SelectItem>)}</SelectGroup>}
          </SelectContent>
        </Select></div>
      {chosen && <AddressCard a={chosen} compact testid={`${testid}-card`} />}
    </div>
  );
}
